import { Component, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Alert, Button, CircularProgress, Typography } from '@/ui';

import HideCancelledToggle from '../coachSchedule/HideCancelledToggle';
import { dayKeyFromIso } from '../coachSchedule/calendarGrid';

import AdminMemberSessionCard from './AdminMemberSessionCard';
import {
  adminMemberRowEndAt,
  adminMemberRowIsCancelled,
  adminMemberRowStartAt,
  findAdminMemberSessionAnchor,
} from './composeAdminMemberSessionRows';

import type {
  AdminMemberSessionDiagnostic,
  AdminMemberSessionRow,
} from './composeAdminMemberSessionRows';
import type { ReactNode } from 'react';

export interface AdminMemberSessionTimelineProps {
  rows: AdminMemberSessionRow[];
  diagnostics: AdminMemberSessionDiagnostic[];
  status: 'loading' | 'ready' | 'partial' | 'error';
  isRefreshing?: boolean;
  errorSources: string[];
  scopeKey: string;
  timeZone: string;
  now: number;
  onRetry: () => void;
  // MEN-5338: Ops actions on actionable native rows.
  onCancel?: (row: AdminMemberSessionRow, target: HTMLElement) => void;
  onReschedule?: (row: AdminMemberSessionRow, target: HTMLElement) => void;
  pendingRowKey?: string | null;
}

function enclosingScroller(element: HTMLElement): Element {
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    const style = getComputedStyle(parent);
    if (/(auto|scroll|overlay)/u.test(style.overflowY || style.overflow))
      return parent;
  }
  return document.scrollingElement ?? document.documentElement;
}

// Hooks have no pre-mutation snapshot lifecycle. Capture the old DOM at commit
// time so parent-driven refreshes are safe even with concurrent rendering.
class BeforeUpdate extends Component<{
  capture: () => void;
  children: ReactNode;
}> {
  override getSnapshotBeforeUpdate() {
    const { capture } = this.props;
    capture();
    return null;
  }

  override componentDidUpdate() {
    /* Restoration runs in the owning component's layout effect. */
  }

  override render() {
    const { children } = this.props;
    return children;
  }
}

const Timeline = ({
  rows,
  now,
  timeZone,
  status,
  isRefreshing,
  diagnostics,
  errorSources,
  onRetry,
  onCancel,
  onReschedule,
  pendingRowKey = null,
}: Omit<AdminMemberSessionTimelineProps, 'scopeKey'>) => {
  const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
  });
  const ordered = [...rows].sort(
    (a, b) =>
      Date.parse(adminMemberRowStartAt(a)) -
        Date.parse(adminMemberRowStartAt(b)) || a.key.localeCompare(b.key),
  );
  const anchor = findAdminMemberSessionAnchor(ordered, now);
  const index = Math.max(
    0,
    ordered.findIndex((row) => row.key === anchor),
  );
  const browsed = useRef(false);
  const settled = useRef<{
    status: 'ready' | 'partial';
    anchor: string | null;
  } | null>(null);
  const shouldAnchor =
    !browsed.current &&
    (status === 'ready' || status === 'partial') &&
    Boolean(anchor) &&
    (!settled.current ||
      (settled.current.status === 'partial' &&
        status === 'ready' &&
        settled.current.anchor !== anchor));
  const [range, setRange] = useState<string[] | null>(null);
  const windowKeys = useRef<string[]>([]);
  const retained = new Set(shouldAnchor ? [] : (range ?? windowKeys.current));
  const indices = ordered.flatMap((row, i) =>
    retained.has(row.key) ? [i] : [],
  );
  const historyAnchor =
    ordered[index] && Date.parse(adminMemberRowEndAt(ordered[index])) < now;
  const start = indices.length
    ? Math.min(...indices)
    : Math.max(0, index - (historyAnchor ? 14 : 15));
  const end = indices.length
    ? Math.max(...indices) + 1
    : Math.min(ordered.length, index + 15);
  const visible = ordered.slice(start, end);
  const elements = useRef(new Map<string, HTMLLIElement>());
  const section = useRef<HTMLElement>(null);
  const snapshot = useRef<{
    scroller: Element;
    positions: { key: string; offset: number }[];
  } | null>(null);
  const capture = () => {
    if (!section.current) return;
    const scroller = enclosingScroller(section.current);
    const top =
      scroller === document.scrollingElement ||
      scroller === document.documentElement
        ? 0
        : scroller.getBoundingClientRect().top + scroller.clientTop;
    const height =
      scroller === document.scrollingElement ||
      scroller === document.documentElement
        ? window.innerHeight
        : scroller.getBoundingClientRect().height;
    const positions = [...elements.current].map(([key, element]) => ({
      key,
      rect: element.getBoundingClientRect(),
    }));
    positions.sort((a, b) => a.rect.top - b.rect.top);
    const firstVisible = positions.findIndex(
      ({ rect }) => rect.bottom > top && rect.top < top + height,
    );
    if (firstVisible < 0) {
      snapshot.current = null;
      return;
    }
    snapshot.current = {
      scroller,
      positions: positions
        .map(({ key, rect }, i) => ({
          key,
          offset: rect.top - top,
          distance: Math.abs(i - firstVisible),
          visible: rect.bottom > top && rect.top < top + height,
        }))
        .sort(
          (a, b) =>
            Number(b.visible) - Number(a.visible) || a.distance - b.distance,
        ),
    };
  };
  const older = useRef<HTMLDivElement>(null);
  const newer = useRef<HTMLDivElement>(null);
  const revealing = useRef(false);
  const reveal = (direction: 'older' | 'newer') => {
    if (revealing.current) return;
    revealing.current = true;
    setRange(
      ordered
        .slice(
          direction === 'older' ? Math.max(0, start - 15) : start,
          direction === 'newer' ? Math.min(ordered.length, end + 15) : end,
        )
        .map((row) => row.key),
    );
  };
  useLayoutEffect(() => {
    revealing.current = false;
    windowKeys.current = visible.map((row) => row.key);
    const previous = snapshot.current;
    snapshot.current = null;
    if (!previous) return;
    const position = previous.positions.find(({ key }) =>
      elements.current.has(key),
    );
    if (!position) return;
    const element = elements.current.get(position.key);
    if (!element) return;
    const top =
      previous.scroller === document.scrollingElement ||
      previous.scroller === document.documentElement
        ? 0
        : previous.scroller.getBoundingClientRect().top +
          previous.scroller.clientTop;
    previous.scroller.scrollTop +=
      element.getBoundingClientRect().top - top - position.offset;
  });
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          if (entry.target === older.current) reveal('older');
          if (entry.target === newer.current) reveal('newer');
        }
      }
    });
    if (older.current) observer.observe(older.current);
    if (newer.current) observer.observe(newer.current);
    return () => observer.disconnect();
  }, [start, end, rows]);
  useEffect(() => {
    if (!section.current) return undefined;
    const scroller = enclosingScroller(section.current);
    const target =
      scroller === document.scrollingElement ||
      scroller === document.documentElement
        ? document
        : scroller;
    const mark = () => {
      browsed.current = true;
    };
    const key = (event: Event) => {
      if (!(event instanceof KeyboardEvent)) return;
      const element = event.target;
      if (
        element instanceof HTMLElement &&
        (element.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/u.test(element.tagName))
      )
        return;
      if (
        [
          'ArrowUp',
          'ArrowDown',
          'PageUp',
          'PageDown',
          'Home',
          'End',
          ' ',
        ].includes(event.key)
      )
        mark();
    };
    let scrollbarIntent = false;
    const pointer = (event: Event) => {
      scrollbarIntent = event.target === scroller;
    };
    const scroll = () => {
      if (scrollbarIntent) mark();
    };
    target.addEventListener('wheel', mark, { passive: true });
    target.addEventListener('touchmove', mark, { passive: true });
    target.addEventListener('keydown', key);
    target.addEventListener('pointerdown', pointer);
    target.addEventListener('scroll', scroll);
    const release = () => {
      scrollbarIntent = false;
    };
    target.addEventListener('pointerup', release);
    return () => {
      target.removeEventListener('wheel', mark);
      target.removeEventListener('touchmove', mark);
      target.removeEventListener('keydown', key);
      target.removeEventListener('pointerdown', pointer);
      target.removeEventListener('scroll', scroll);
      target.removeEventListener('pointerup', release);
    };
  }, []);
  useLayoutEffect(() => {
    if (
      shouldAnchor &&
      anchor &&
      (!settled.current ||
        (settled.current.status === 'partial' &&
          status === 'ready' &&
          settled.current.anchor !== anchor))
    ) {
      elements.current.get(anchor)?.scrollIntoView({ block: 'start' });
    }
    if ((status === 'ready' || status === 'partial') && anchor)
      settled.current = { status, anchor };
  });
  const incomplete =
    status === 'partial' || status === 'error' || errorSources.length > 0;
  return (
    <BeforeUpdate capture={capture}>
      <section
        ref={section}
        aria-label="Member session timeline"
        className="w-full min-w-0"
      >
        {(status === 'loading' || isRefreshing) && (
          <CircularProgress
            aria-label={
              isRefreshing
                ? 'Refreshing member sessions'
                : 'Loading member sessions'
            }
            size={24}
          />
        )}
        {incomplete && (
          <Alert
            action={
              <Button aria-label="Retry member sessions" onClick={onRetry}>
                Retry
              </Button>
            }
            severity="warning"
          >
            Session history is incomplete. Some sources could not be loaded;
            displayed sessions may be retained from the last successful load.
          </Alert>
        )}
        {diagnostics.length > 0 && (
          <Typography role="status" variant="body2">
            Some session records could not be matched safely and are shown
            separately when available.
          </Typography>
        )}
        {status === 'ready' &&
          !incomplete &&
          diagnostics.length === 0 &&
          rows.length === 0 && (
            <Typography variant="body2">No sessions</Typography>
          )}
        {start > 0 && (
          <div ref={older} data-reveal="older">
            <Button
              onClick={() => {
                browsed.current = true;
                reveal('older');
              }}
            >
              Load older sessions
            </Button>
          </div>
        )}
        <ul
          aria-label={`Member sessions (${timeZone})`}
          className="m-0 flex w-full min-w-0 list-none flex-col gap-4 p-0"
        >
          {visible.map((row, i) => (
            <li
              key={row.key}
              ref={(element) => {
                if (element) elements.current.set(row.key, element);
                else elements.current.delete(row.key);
              }}
              className="flex min-w-0 flex-col gap-2 sm:flex-row sm:gap-5"
              data-session-key={row.key}
            >
              <time
                aria-label={`${new Intl.DateTimeFormat('en-US', { dateStyle: 'full', timeZone }).format(new Date(adminMemberRowStartAt(row)))} (${timeZone})`}
                className="shrink-0 sm:basis-28"
                dateTime={adminMemberRowStartAt(row)}
              >
                {(!visible[i - 1] ||
                  dayKeyFromIso(adminMemberRowStartAt(row), timeZone) !==
                    dayKeyFromIso(
                      adminMemberRowStartAt(visible[i - 1]!),
                      timeZone,
                    )) && (
                  <>
                    <Typography variant="body2">
                      {weekdayFormatter.format(
                        new Date(adminMemberRowStartAt(row)),
                      )}
                      ,
                    </Typography>
                    <Typography variant="body2">
                      {new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone,
                      }).format(new Date(adminMemberRowStartAt(row)))}
                    </Typography>
                  </>
                )}
              </time>
              <AdminMemberSessionCard
                actionPending={pendingRowKey === row.key}
                now={now}
                row={row}
                timeZone={timeZone}
                onCancel={onCancel && ((target) => onCancel(row, target))}
                onReschedule={
                  onReschedule && ((target) => onReschedule(row, target))
                }
              />
            </li>
          ))}
        </ul>
        {end < ordered.length && (
          <div ref={newer} data-reveal="newer">
            <Button
              onClick={() => {
                browsed.current = true;
                reveal('newer');
              }}
            >
              Load newer sessions
            </Button>
          </div>
        )}
      </section>
    </BeforeUpdate>
  );
};
const AdminMemberSessionTimeline = ({
  rows,
  scopeKey,
  ...props
}: AdminMemberSessionTimelineProps) => {
  const [hideCancelled, setHideCancelled] = useState(false);
  // MEN-5349: filter before Timeline so its window and anchor see only the
  // displayed rows; re-key so toggling re-anchors instead of retaining keys.
  const displayedRows = hideCancelled
    ? rows.filter((row) => !adminMemberRowIsCancelled(row))
    : rows;
  return (
    <>
      {/* Figma: top left, aligned with the cards past the date gutter. */}
      <div className="flex sm:pl-[8.25rem]">
        <HideCancelledToggle
          checked={hideCancelled}
          onChange={setHideCancelled}
        />
      </div>
      <Timeline
        key={`${scopeKey}:${hideCancelled ? 'hide' : 'show'}-cancelled`}
        rows={displayedRows}
        {...props}
      />
    </>
  );
};

export default AdminMemberSessionTimeline;
